import { Controller } from '@hotwired/stimulus'
import consumer from '../channels/consumer'

const STEP_ORDER = ['offered', 'assigned', 'picked_up', 'delivered']

export default class extends Controller {
  static targets = ['map', 'status', 'headline', 'detail', 'riderName', 'riderPhone', 'steps']
  static values = {
    orderId: Number,
    apiKey: String,
    enableMap: { type: Boolean, default: false },
    subscribe: { type: Boolean, default: true },
    shopLat: Number,
    shopLng: Number,
    dropLat: Number,
    dropLng: Number,
    riderLat: Number,
    riderLng: Number,
    status: String
  }

  connect () {
    if (this.enableMapValue && this.apiKeyValue && this.hasMapTarget) {
      this.loadGoogleMaps()
        .then(() => this.initMap())
        .then(() => this.subscribe())
        .catch(() => {
          this.setStatus('Could not load Google Maps.')
          this.subscribe()
        })
      return
    }

    this.setStatus('Listening for rider updates…')
    this.subscribe()
  }

  disconnect () {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  loadGoogleMaps () {
    if (window.google?.maps) return Promise.resolve()

    return new Promise((resolve, reject) => {
      const existing = document.getElementById('google-maps-js')
      if (existing) {
        if (window.google?.maps) {
          resolve()
          return
        }
        existing.addEventListener('load', () => resolve(), { once: true })
        existing.addEventListener('error', reject, { once: true })
        return
      }

      const script = document.createElement('script')
      script.id = 'google-maps-js'
      script.async = true
      script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(this.apiKeyValue)}`
      script.onload = () => resolve()
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  initMap () {
    const center = this.validCoord(this.dropLatValue, this.dropLngValue)
      ? { lat: this.dropLatValue, lng: this.dropLngValue }
      : this.validCoord(this.shopLatValue, this.shopLngValue)
        ? { lat: this.shopLatValue, lng: this.shopLngValue }
        : { lat: 31.5204, lng: 74.3587 }

    this.map = new window.google.maps.Map(this.mapTarget, {
      center,
      zoom: 14,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    })

    if (this.validCoord(this.shopLatValue, this.shopLngValue)) {
      this.shopMarker = new window.google.maps.Marker({
        map: this.map,
        position: { lat: this.shopLatValue, lng: this.shopLngValue },
        title: 'Shop',
        label: { text: 'S', color: 'white', fontWeight: '700' },
        icon: this.pinIcon('#0284c7')
      })
    }

    if (this.validCoord(this.dropLatValue, this.dropLngValue)) {
      this.dropMarker = new window.google.maps.Marker({
        map: this.map,
        position: { lat: this.dropLatValue, lng: this.dropLngValue },
        title: 'Your address',
        label: { text: 'D', color: 'white', fontWeight: '700' },
        icon: this.pinIcon('#4f46e5')
      })
    }

    if (this.validCoord(this.riderLatValue, this.riderLngValue)) {
      this.moveRider(this.riderLatValue, this.riderLngValue)
      this.setStatus('Live map ready')
    } else {
      this.setStatus('Waiting for rider GPS…')
    }

    this.fitBounds()
  }

  pinIcon (color) {
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 10
    }
  }

  riderIcon () {
    return {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      fillColor: '#059669',
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 6
    }
  }

  subscribe () {
    if (!this.subscribeValue || !this.orderIdValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: 'OrderTrackingChannel', order_id: this.orderIdValue },
      {
        received: (data) => this.handleMessage(data)
      }
    )
  }

  handleMessage (data) {
    if (!data?.type) return

    if (data.assignment_status) {
      this.updateSteps(data.assignment_status)
    }

    if (data.headline && this.hasHeadlineTarget) {
      this.headlineTarget.textContent = data.headline
    }
    if (data.detail && this.hasDetailTarget) {
      this.detailTarget.textContent = data.detail
    }
    if (data.rider_name && this.hasRiderNameTarget) {
      this.riderNameTarget.textContent = data.rider_name
    }
    if (data.rider_phone && this.hasRiderPhoneTarget) {
      this.riderPhoneTarget.textContent = data.rider_phone
      this.riderPhoneTarget.href = `tel:${String(data.rider_phone).replace(/[^\d+]/g, '')}`
    }

    if (data.type === 'rider_location') {
      this.moveRider(data.latitude, data.longitude)
      this.setStatus(`Rider live · ${this.friendlyStatus(data.assignment_status)}`)
      return
    }

    if (data.type === 'assignment_status') {
      this.setStatus(this.friendlyStatus(data.assignment_status))
      if (this.validCoord(data.rider_lat, data.rider_lng)) {
        this.moveRider(data.rider_lat, data.rider_lng)
      }
    }
  }

  updateSteps (status) {
    if (!this.hasStepsTarget) return
    const current = STEP_ORDER.indexOf(String(status))
    this.stepsTarget.querySelectorAll('[data-step]').forEach((el) => {
      const index = STEP_ORDER.indexOf(el.dataset.step)
      const done = current >= 0 && index >= 0 && index <= current
      const dot = el.querySelector('[data-step-dot]')
      const label = el.querySelector('[data-step-label]')
      if (dot) {
        dot.className = done
          ? 'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-extrabold ring-2 transition bg-slate-900 text-white ring-slate-900'
          : 'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-[11px] font-extrabold ring-2 transition bg-slate-100 text-slate-400 ring-slate-200'
      }
      if (label) {
        label.className = done
          ? 'mt-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-800'
          : 'mt-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-400'
      }
    })
  }

  friendlyStatus (status) {
    switch (String(status)) {
      case 'offered': return 'assigned'
      case 'assigned': return 'heading to shop'
      case 'picked_up': return 'on the way to you'
      case 'delivered': return 'delivered'
      default: return status || 'updating'
    }
  }

  moveRider (lat, lng) {
    if (!this.map || !this.validCoord(lat, lng)) return
    const position = { lat: Number(lat), lng: Number(lng) }

    if (!this.riderMarker) {
      this.riderMarker = new window.google.maps.Marker({
        map: this.map,
        position,
        title: 'Rider',
        label: { text: 'R', color: 'white', fontWeight: '700' },
        icon: this.riderIcon()
      })
    } else {
      this.riderMarker.setPosition(position)
    }

    this.map.panTo(position)
    this.fitBounds()
  }

  fitBounds () {
    if (!this.map || !window.google?.maps) return
    const bounds = new window.google.maps.LatLngBounds()
    let count = 0
    ;[this.shopMarker, this.dropMarker, this.riderMarker].forEach((marker) => {
      if (!marker) return
      bounds.extend(marker.getPosition())
      count += 1
    })
    if (count >= 2) this.map.fitBounds(bounds, 64)
  }

  validCoord (lat, lng) {
    const latitude = Number(lat)
    const longitude = Number(lng)
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false
    if (latitude === 0 && longitude === 0) return false
    return Math.abs(latitude) <= 90 && Math.abs(longitude) <= 180
  }

  setStatus (text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
